// src/pages/pedidos/VerPedido.js
import React, { useEffect, useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import { Offcanvas, Badge, Card, Table, Alert } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import { LoadingSpinner } from 'components/LoadingSpinner'
import api from 'api/api'
import { toast } from 'react-toastify'

import {
	getTipoColor,
	OPCIONES_TIPOS_PEDIDO_PARA_FILTRO,
	calcularEstados,
	calcularEstadoPrincipal
} from 'config/pedidos'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { API_UPLOADS_URL } from 'config/general'


import './pedidos.css'
import { getDisplayDateTime } from 'utils/date'
import { CronologiaEstados } from 'components/CronologiaEstados'


function VerPedido(props) {
	const { mostrar, pedido, cancelar, soloLectura = false } = props
	const [pedidoDetalle, setPedidoDetalle] = useState(null)
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (pedido && mostrar) {
			cargarDetallePedido()
		} else {
			setPedidoDetalle(null)
		}
	}, [pedido, mostrar])

	const cargarDetallePedido = async () => {
		if (!pedido?.id) return

		try {
			setLoading(true)
			const { datos } = await api.get(`a/pedidos/${pedido.id}`)

			// Calcular estados para el detalle del pedido
			const pedidoConEstados = {
				...datos,
				estados: calcularEstados(datos),
				estadoPrincipal: calcularEstadoPrincipal(datos)
			}

			setPedidoDetalle(pedidoConEstados)
		} catch (error) {
			toast.error('Error al cargar los detalles del pedido')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const handleClose = () => {
		setPedidoDetalle(null)
		cancelar()
	}

	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	const renderEstadoPago = (pedidoData) => {
		const estados = calcularEstados(pedidoData)

		return (
			<div className="estado-pago-detalle">
				<div className="mb-3">
					{estados.pagado ? (
						<Badge bg="success" className="p-2">
							<i className="fa fa-credit-card me-2"></i>
							<strong>PAGADO</strong>
						</Badge>
					) : (
						<Badge bg="warning" className="p-2">
							<i className="fa fa-exclamation-triangle me-2"></i>
							<strong>PENDIENTE DE PAGO</strong>
						</Badge>
					)}
				</div>

				{/* Detalles del pago si existe */}
				{pedidoData.pago && pedidoData.pago.id && (
					<div className="detalles-pago">
						<h6 className="mb-2">Información del Pago</h6>
						<div className="info-pago">
							<div className="mb-2">
								<strong>Tipo:</strong> {pedidoData.pago.tipo || 'No especificado'}
							</div>

							{/* Detalles de Bancard si existen */}
							{pedidoData.pago.detallesBancard?.responseCode && (
								<div className="detalles-bancard">
									<div className="mb-2">
										<strong>Proveedor:</strong> Bancard
									</div>
									<div className="mb-2">
										<strong>Estado de la transacción:</strong>{' '}
										{pedidoData.pago.detallesBancard.response_code === '00' ? (
											<span className="text-success">
												<i className="fa fa-check-circle me-1"></i>
												Aprobada
											</span>
										) : (
											<span className="text-danger">
												<i className="fa fa-times-circle me-1"></i>
												Código: {pedidoData.pago.detallesBancard.response_code}
											</span>
										)}
									</div>
									{pedidoData.pago.detallesBancard.authorization_number && (
										<div className="mb-2">
											<strong>Nº de Autorización:</strong> {pedidoData.pago.detallesBancard.authorization_number}
										</div>
									)}
									{pedidoData.pago.detallesBancard.ticket_number && (
										<div className="mb-2">
											<strong>Nº de Ticket:</strong> {pedidoData.pago.detallesBancard.ticket_number}
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		)
	}

	const renderMapa = (puntoEntrega) => {
		// Generar URL de Google Maps
		const direccionCompleta = [
			puntoEntrega.direccion,
			puntoEntrega.barrio?.nombre,
			puntoEntrega.ciudad?.nombre,
			'Paraguay'
		].filter(Boolean).join(', ')

		const googleMapsUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyCaw8ahy7ztFKt6aq9kbqVlM1Bk_YmUU9s&q=${encodeURIComponent(direccionCompleta)}`
		const googleMapsLinkUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccionCompleta)}`

		return (
			<div className="mapa-container">
				{/* Placeholder del mapa - necesitarás configurar Google Maps API */}
				<div className="mapa-placeholder bg-light border rounded p-4 text-center mb-3">
					<i className="fa fa-map-marker-alt fa-3x text-muted mb-2"></i>
					<p className="text-muted mb-2">Vista del mapa</p>
					<small className="text-muted">{direccionCompleta}</small>
				</div>

				{/* Enlaces para abrir en Google Maps */}
				<div className="text-center">
					<a
						href={googleMapsLinkUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="btn btn-outline-primary btn-sm"
					>
						<i className="fa fa-external-link-alt me-1"></i>
						Abrir en Google Maps
					</a>
				</div>
			</div>
		)
	}

	const calcularSubtotal = (items) => {
		if (!items || items.length === 0) return 0
		return items.reduce((total, item) => total + (parseFloat(item.monto) * parseInt(item.cantidad)), 0)
	}

	// No renderizar nada si no hay pedido o no se está mostrando
	if (!mostrar) {
		return null
	}

	return (
		<Offcanvas show={mostrar} onHide={handleClose} placement="end" className="pedido-detalle-panel" style={{ width: '70%' }}>
			<PanelHeader
				onClick={handleClose}
				iconClass="fa-arrow-left"
				btnText="Atrás"
				text={`Detalles del Pedido #${pedido?.id || ''}`}
			/>

			<Offcanvas.Body>
				{loading ? (
					<div className="text-center py-5">
						<LoadingSpinner />
						<p className="mt-3">Cargando detalles del pedido...</p>
					</div>
				) : pedidoDetalle ? (
					<Row>
						<Col sm={12}>
							{/* Información general del pedido */}
							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">
										<i className="fa fa-info-circle me-2"></i>
										Información General
									</h5>
								</Card.Header>
								<Card.Body>
									<Row>
										<Col sm={4}>
											<div className="pedido-detalle-info">
												<div className="info-card">
													<div className="info-label">ID del Pedido</div>
													<div className="info-value">#{pedidoDetalle.id}</div>
												</div>

												<div className="info-card">
													<div className="info-label">Fecha de Creación</div>
													<div className="info-value">{getDisplayDateTime(pedidoDetalle.recibido)}</div>
												</div>

												<div className="info-card">
													<div className="info-label">Tipo de Pedido</div>
													<div className="info-value">
														<Badge bg={getTipoColor(pedidoDetalle.tipo)} className="badge-tipo">
															{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.find(t => t.value === pedidoDetalle.tipo)?.label || pedidoDetalle.tipo}
														</Badge>
													</div>
												</div>
											</div>
										</Col>

										<Col sm={4}>
											<div className="pedido-detalle-info">
												<div className="info-card">
													<div className="info-label">Estado de Pago</div>
													<div className="info-value">
														{renderEstadoPago(pedidoDetalle)}
													</div>
												</div>

												{pedidoDetalle.motivoCancelado && (
													<Alert variant="warning" className="mt-3">
														<strong>Motivo de cancelación:</strong><br />
														{pedidoDetalle.motivoCancelado}
													</Alert>
												)}
											</div>
										</Col>

										<Col sm={4}>
											<div className="pedido-detalle-info">
												<div className="info-card">
													<div className="info-label">Total del Pedido</div>
													<div className="info-value">
														<h4 className="text-primary mb-0">{formatearPrecio(pedidoDetalle.montoTotal)}</h4>
													</div>
												</div>
											</div>
										</Col>
									</Row>
								</Card.Body>
							</Card>

							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">
										<i className="fa fa-user me-2"></i>
										Información del Cliente
									</h5>
								</Card.Header>
								<Card.Body>
									<Row>
										<Col sm={6}>
											{pedidoDetalle.usuario ? (
												<div className="info-card">
													<div className="info-label">Cliente</div>
													<div className="info-value">
														<strong>{pedidoDetalle.usuario.nombre} {pedidoDetalle.usuario.apellido}</strong>
													</div>
													{pedidoDetalle.usuario.telefono && (
														<div className="info-value text-muted">
															<i className="fa fa-phone me-1"></i>
															{pedidoDetalle.usuario.telefono}
														</div>
													)}
													{pedidoDetalle.usuario.email && (
														<div className="info-value text-muted">
															<i className="fa fa-envelope me-1"></i>
															{pedidoDetalle.usuario.email}
														</div>
													)}
												</div>
											) : (
												<div className="info-card">
													<div className="info-label">Cliente</div>
													<div className="info-value text-muted">Cliente no especificado</div>
												</div>
											)}
										</Col>

										<Col sm={6}>
											{pedidoDetalle.datosFacturacion && (
												<div className="info-card">
													<div className="info-label">Datos de Facturación</div>
													{pedidoDetalle.datosFacturacion.razonSocial && (
														<div className="info-value">
															<strong>Razón Social:</strong> {pedidoDetalle.datosFacturacion.razonSocial}
														</div>
													)}
													{pedidoDetalle.datosFacturacion.ruc && (
														<div className="info-value">
															<strong>RUC:</strong> {pedidoDetalle.datosFacturacion.ruc}
														</div>
													)}
												</div>
											)}
										</Col>
									</Row>
								</Card.Body>
							</Card>

							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">
										<i className="fa fa-comments me-2"></i>
										Observaciones
									</h5>
								</Card.Header>
								<Card.Body>
									<Row>
										{/* Observaciones del Cliente - SIEMPRE MOSTRAR */}
										<Col sm={6}>
											<div className="info-card border-primary">
												<div className="info-label text-primary">
													<i className="fa fa-user me-2"></i>
													Observaciones del Cliente
												</div>
												<div className="info-value">
													{pedidoDetalle.observaciones ? (
														<div className="p-3 bg-light rounded">
															<em>"{pedidoDetalle.observaciones}"</em>
														</div>
													) : (
														<div className="p-3 bg-light border rounded text-center text-muted">
															<i className="fa fa-minus me-1"></i>
														</div>
													)}
												</div>
												<small className="text-primary">
													<i className="fa fa-info-circle me-1"></i>
													{pedidoDetalle.observaciones
														? "Comentarios que dejó el cliente al realizar el pedido"
														: "El cliente no dejó observaciones adicionales"
													}
												</small>
											</div>
										</Col>

										{/* Observaciones Internas */}
										<Col sm={6}>
											<div className="info-card border-warning">
												<div className="info-label text-warning">
													<i className="fa fa-lock me-2"></i>
													Observaciones Internas
												</div>
												<div className="info-value">
													{pedidoDetalle.observacionesInternas ? (
														<div className="p-3 bg-warning bg-opacity-10 border-warning rounded">
															<strong>"{pedidoDetalle.observacionesInternas}"</strong>
														</div>
													) : (
														<div className="p-3 bg-warning bg-opacity-10 border-warning rounded text-center text-muted">
															<i className="fa fa-minus me-1"></i>
														</div>
													)}
												</div>
												<small className="text-warning">
													<i className="fa fa-eye-slash me-1"></i>
													{pedidoDetalle.observacionesInternas
														? "Notas internas del equipo (no visibles para el cliente)"
														: "No hay notas internas registradas para este pedido"
													}
												</small>
											</div>
										</Col>
									</Row>
								</Card.Body>
							</Card>

							{/* Punto de entrega con mapa */}
							{pedidoDetalle.puntoEntrega && (
								<Card className="mb-4 shadow-sm">
									<Card.Header className="bg-light">
										<h5 className="mb-0">
											<i className="fa fa-map-marker-alt me-2"></i>
											Punto de Entrega
										</h5>
									</Card.Header>
									<Card.Body>
										<Row>
											<Col sm={6}>
												<div className="info-card">
													<div className="info-value">
														<strong>{pedidoDetalle.puntoEntrega.direccion}</strong>
													</div>
													{pedidoDetalle.puntoEntrega.barrio && (
														<div className="info-value text-muted">
															{pedidoDetalle.puntoEntrega.barrio.nombre}
														</div>
													)}
													{pedidoDetalle.puntoEntrega.ciudad && (
														<div className="info-value text-muted">
															{pedidoDetalle.puntoEntrega.ciudad.nombre}
														</div>
													)}
													{pedidoDetalle.puntoEntrega.observaciones && (
														<div className="info-value text-muted mt-2">
															<strong>Observaciones:</strong> {pedidoDetalle.puntoEntrega.observaciones}
														</div>
													)}
												</div>
											</Col>
											<Col sm={6}>
												{renderMapa(pedidoDetalle.puntoEntrega)}
											</Col>
										</Row>
									</Card.Body>
								</Card>
							)}

							{/* Items del pedido */}
							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">
										<i className="fa fa-shopping-cart me-2"></i>
										Items del Pedido ({pedidoDetalle.items?.length || 0})
									</h5>
								</Card.Header>
								<Card.Body>
									{pedidoDetalle.items && pedidoDetalle.items.length > 0 ? (
										<Table striped responsive>
											<thead>
												<tr>
													<th style={{ width: '80px' }}>Imagen</th>
													<th>Producto</th>
													<th className="text-center" style={{ width: '100px' }}>Cantidad</th>
													<th className="text-end" style={{ width: '120px' }}>Precio Unit.</th>
													<th className="text-end" style={{ width: '120px' }}>Total</th>
												</tr>
											</thead>
											<tbody>
												{pedidoDetalle.items.map((item, index) => (
													<tr key={item.pedidoItemId || item.id || index}>
														<td>
															{item.multimedia && item.multimedia.length > 0 ? (
																<img
																	src={API_UPLOADS_URL + item.multimedia[0].url}
																	alt={item.nombre}
																	className="img-thumbnail"
																	style={{ width: '40px', height: '40px', objectFit: 'cover' }}
																/>
															) : (
																<div
																	className="bg-light d-flex align-items-center justify-content-center rounded"
																	style={{ width: '40px', height: '40px' }}
																>
																	<i className="fa fa-image text-muted"></i>
																</div>
															)}
														</td>
														<td>
															<div className="fw-bold">{item.nombre}</div>
															{item.codigo && (
																<small className="text-muted d-block">Código: {item.codigo}</small>
															)}
															{item.atributosSeleccionados && item.atributosSeleccionados.length > 0 && (
																<div className="mt-1">
																	{item.atributosSeleccionados.map((attr, attrIndex) => (
																		<Badge
																			key={attrIndex}
																			bg="info"
																			className="me-1 mb-1"
																			style={{ fontSize: '0.75rem' }}
																		>
																			{attr.tipoNombre}: {attr.valorNombre}
																		</Badge>
																	))}
																</div>
															)}
														</td>
														<td className="text-center">
															<Badge bg="secondary" className="fs-6">
																{item.cantidad}
															</Badge>
														</td>
														<td className="text-end">
															{formatearPrecio(item.monto)}
														</td>
														<td className="text-end fw-bold text-primary">
															{formatearPrecio(parseFloat(item.monto) * parseInt(item.cantidad))}
														</td>
													</tr>
												))}
											</tbody>
										</Table>
									) : (
										<div className="text-center py-4">
											<i className="fa fa-shopping-cart fa-3x text-muted mb-3"></i>
											<p className="text-muted mb-0">No hay items en este pedido</p>
										</div>
									)}
								</Card.Body>
							</Card>

							{/* Resumen de totales */}
							<Card className="mb-4 shadow-sm">
								<Card.Header className="bg-light">
									<h5 className="mb-0">
										<i className="fa fa-calculator me-2"></i>
										Resumen de Totales
									</h5>
								</Card.Header>
								<Card.Body>
									<Table borderless className="mb-0">
										<tbody>
											<tr>
												<td className="text-end"><strong>Subtotal:</strong></td>
												<td className="text-end" style={{ width: '150px' }}>
													{formatearPrecio(calcularSubtotal(pedidoDetalle.items))}
												</td>
											</tr>
											{pedidoDetalle.costoEnvio > 0 && (
												<tr>
													<td className="text-end">
														<strong>Envío:</strong>
														{pedidoDetalle.costoEnvioManual && (
															<Badge bg="warning" className="ms-2" style={{ fontSize: '0.7rem' }}>
																<i className="fa fa-edit me-1"></i>
																Manual
															</Badge>
														)}
													</td>
													<td className="text-end">
														{formatearPrecio(pedidoDetalle.costoEnvio)}
													</td>
												</tr>
											)}
											<tr className="border-top">
												<td className="text-end"><h5><strong>Total:</strong></h5></td>
												<td className="text-end">
													<h5 className="text-primary"><strong>{formatearPrecio(pedidoDetalle.montoTotal)}</strong></h5>
												</td>
											</tr>
										</tbody>
									</Table>
								</Card.Body>
							</Card>

							{/* Cronología de Estados */}
							<CronologiaEstados pedido={pedidoDetalle} soloLectura={true} />
						</Col>
					</Row>
				) : (
					<div className="text-center py-5">
						<p className="text-muted">No se pudieron cargar los detalles del pedido</p>
					</div>
				)}
			</Offcanvas.Body>

			<PanelFooter>
				<Button
					variant="primary"
					onClick={handleClose}
					className="w-100"
				>
					Cerrar
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default VerPedido